<script setup lang="ts">
import { computed } from 'vue'
import { Check, ChevronsUpDown, Search } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import {
  Button,
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/shadcn'

export interface ComboboxOption {
  value: string | number
  label: string
}

type StyleType =
  | 'default'   // 기본 스타일
  | 'error'     // 에러 상태
  | 'success'   // 성공 상태
  | 'warning'   // 경고 상태
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기

const props = withDefaults(
  defineProps<{
    modelValue?: ComboboxOption
    options?: ComboboxOption[]
    placeholder?: string
    searchPlaceholder?: string
    emptyText?: string
    disabled?: boolean
    required?: boolean
    name?: string
    id?: string
    multiple?: boolean
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    options: () => [],
    placeholder: '선택해주세요.',
    searchPlaceholder: '',
    emptyText: '검색 결과가 없습니다.',
    disabled: false,
    required: false,
    multiple: false,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: ComboboxOption | undefined]
  'change': [value: ComboboxOption | undefined]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

/**
 * styletype -> variant/class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { variant: 'default' | 'outline' | 'destructive' | 'secondary' | 'ghost' | 'link', buttonClass?: string, inputClass?: string }> = {
  default: { 
    variant: 'outline',
    buttonClass: 'h-7 text-xs px-2.5',
  },
  error: { 
    variant: 'outline',
    buttonClass: 'h-7 text-xs px-2.5 border-destructive text-destructive',
    inputClass: 'border-destructive focus:ring-destructive',
  },
  success: { 
    variant: 'outline',
    buttonClass: 'h-7 text-xs px-2.5 border-green-500 text-green-700',
    inputClass: 'border-green-500 focus:ring-green-500',
  },
  warning: { 
    variant: 'outline',
    buttonClass: 'h-7 text-xs px-2.5 border-amber-500 text-amber-700',
    inputClass: 'border-amber-500 focus:ring-amber-500',
  },
  sm: { 
    variant: 'outline',
    buttonClass: 'h-7 text-[11px] px-2',
    inputClass: 'h-7 text-[11px] px-2 py-1',
  },
  lg: { 
    variant: 'outline',
    buttonClass: 'h-10 text-sm px-3',
    inputClass: 'h-10 text-sm px-3 py-2',
  },
}

const preset = computed(() => {
  const styleKey = props.styletype || 'default'
  return STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
})

const buttonClass = computed(() => {
  return cn('justify-between', preset.value?.buttonClass, props.class)
})

const inputClass = computed(() => {
  return cn('pl-8 pr-2 py-1 focus-visible:ring-0 border-0 border-b rounded-none h-7 text-xs', preset.value?.inputClass)
})

const handleChange = (value: ComboboxOption | undefined) => {
  emit('update:modelValue', value)
  emit('change', value)
}
</script>

<template>
  <Combobox 
    :model-value="modelValue" 
    by="label" 
    :disabled="disabled"
    :required="required"
    :name="name"
    @update:model-value="(value) => handleChange(value as ComboboxOption | undefined)"
  >
    <ComboboxAnchor as-child>
      <ComboboxTrigger as-child>
        <Button 
          :id="id"
          :variant="preset.variant" 
          :class="buttonClass"
          :disabled="disabled"
          @focus="emit('focus', $event as FocusEvent)"
          @blur="emit('blur', $event as FocusEvent)"
        >
          {{ modelValue?.label ?? placeholder }}
          <ChevronsUpDown class="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxList>
      <div class="relative w-full max-w-sm items-center">
        <ComboboxInput :class="inputClass" :placeholder="searchPlaceholder" />
        <span class="absolute start-0 inset-y-0 flex items-center justify-center px-2.5">
          <Search class="size-3.5 text-muted-foreground" />
        </span>
      </div>

      <ComboboxEmpty>
        {{ emptyText }}
      </ComboboxEmpty>

      <ComboboxGroup>
        <ComboboxItem
          v-for="option in options"
          :key="option.value"
          :value="option"
        >
          {{ option.label }}

          <Check v-if="modelValue?.value === option.value" :class="cn('ml-auto h-3.5 w-3.5')" />
        </ComboboxItem>
      </ComboboxGroup>
    </ComboboxList>
  </Combobox>
</template>
